import type { FilesKeyframeSchemaWithIdNullable } from '@iconik-io/iconik-api/src/services/files';

const refreshMarginMs = 5 * 60 * 1000;
const maxEntries = 2000;

const stableUrls = new Map<string, { url: string; refreshAfter: number }>();

function parseAmzDate(value: string): number | undefined {
  const match = value.match(/^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/);
  if (!match) {
    return undefined;
  }
  const [, year, month, day, hours, minutes, seconds] = match;
  const parsed = Date.parse(
    `${year}-${month}-${day}T${hours}:${minutes}:${seconds}Z`,
  );
  return Number.isNaN(parsed) ? undefined : parsed;
}

function getExpiresAt(url: URL): number | undefined {
  const signedAtParam = url.searchParams.get('X-Amz-Date');
  const lifetimeParam = url.searchParams.get('X-Amz-Expires');
  if (signedAtParam && lifetimeParam) {
    const signedAt = parseAmzDate(signedAtParam);
    const lifetimeSeconds = Number(lifetimeParam);
    if (signedAt === undefined || !Number.isFinite(lifetimeSeconds)) {
      return undefined;
    }
    return signedAt + lifetimeSeconds * 1000;
  }

  const expiresParam = url.searchParams.get('Expires');
  if (expiresParam && /^\d+$/.test(expiresParam)) {
    return Number(expiresParam) * 1000;
  }

  return undefined;
}

function isSigned(url: URL): boolean {
  return (
    url.searchParams.has('X-Amz-Signature') || url.searchParams.has('Signature')
  );
}

/**
 * Returns a URL that stays byte-identical for the same media object, so the
 * browser can serve it from its HTTP cache.
 *
 * Every response re-signs media URLs with a new signature and timestamp, and
 * the browser caches by full URL, so an unchanged image is re-downloaded on
 * each response. The first URL seen for an object is reused until it nears its
 * own expiry, which is safe because a signature stays valid until then.
 * Anything unsigned, or signed without a readable expiry, is passed straight
 * through
 */
export function stabilizeMediaUrl<Url extends string | null | undefined>(
  url: Url,
): Url {
  if (!url) {
    return url;
  }

  let parsedUrl: URL;
  try {
    parsedUrl = new URL(url);
  } catch {
    return url;
  }

  if (!isSigned(parsedUrl)) {
    return url;
  }

  const expiresAt = getExpiresAt(parsedUrl);
  if (expiresAt === undefined) {
    return url;
  }

  const key = `${parsedUrl.origin}${parsedUrl.pathname}`;
  const stable = stableUrls.get(key);
  if (stable && Date.now() < stable.refreshAfter) {
    // biome-ignore lint/plugin: the cache is shared across calls, so it stores plain strings; Url is this call's type parameter and every caller instantiates it as string.
    return stable.url as Url;
  }

  if (!stable && stableUrls.size >= maxEntries) {
    const oldestKey = stableUrls.keys().next().value;
    if (oldestKey !== undefined) {
      stableUrls.delete(oldestKey);
    }
  }
  stableUrls.set(key, { url, refreshAfter: expiresAt - refreshMarginMs });

  return url;
}

const stabilizedKeyframes = new WeakMap<
  FilesKeyframeSchemaWithIdNullable[],
  FilesKeyframeSchemaWithIdNullable[]
>();

/**
 * Applies {@link stabilizeMediaUrl} to every keyframe in a list.
 *
 * The same list in gives the same list back, so a consumer can call this while
 * rendering without handing its child a new array each time. A list only
 * changes identity when the store writes a new one, which is also the only
 * moment the URLs inside it could have changed
 */
export function stabilizeKeyframeUrls(
  keyframes: FilesKeyframeSchemaWithIdNullable[] | undefined | null,
): FilesKeyframeSchemaWithIdNullable[] | undefined {
  if (!keyframes) {
    return undefined;
  }

  const stabilized = stabilizedKeyframes.get(keyframes);
  if (stabilized) {
    return stabilized;
  }

  const result = keyframes.map((keyframe) => ({
    ...keyframe,
    url: stabilizeMediaUrl(keyframe.url),
  }));
  stabilizedKeyframes.set(keyframes, result);

  return result;
}
